import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { kr, dato, idag } from '../lib/format';
import { PageHeader, Card, Tabs } from '../components/ui';

const Bokforing = () => {
  const aar = new Date().getFullYear();
  const [fane, setFane] = useState('dagbok');
  const [fra, setFra] = useState(`${aar}-01-01`);
  const [til, setTil] = useState(idag());
  const [data, setData] = useState(null);

  useEffect(() => {
    const url = fane === 'dagbok' ? '/rapport/dagbok'
      : fane === 'hovedbok' ? '/rapport/hovedbok'
      : fane === 'reskontro' ? '/rapport/reskontro'
      : '/rapport/mva-spesifikasjon';
    const params = fane === 'reskontro' ? {} : { fra, til };
    setData(null); // unngå at gammel data fra annen fane render-krasjer ny komponent
    api.get(url, { params }).then((r) => setData(r.data));
  }, [fane, fra, til]);

  return (
    <>
      <PageHeader title="Bokføring" subtitle="Lovpålagte rapporter (bokføringsforskriften)" />

      <Tabs
        value={fane}
        onChange={setFane}
        items={[
          { key: 'dagbok',    label: 'Dagbok' },
          { key: 'hovedbok',  label: 'Hovedbok' },
          { key: 'reskontro', label: 'Reskontro' },
          { key: 'mva',       label: 'MVA-spesifikasjon' },
        ]}
      />

      {fane !== 'reskontro' && (
        <Card style={{ marginBottom: 12 }}>
          <div className="flex" style={{ gap: 12, alignItems: 'flex-end' }}>
            <div><label>Fra</label><input type="date" value={fra} onChange={(e) => setFra(e.target.value)} /></div>
            <div><label>Til</label><input type="date" value={til} onChange={(e) => setTil(e.target.value)} /></div>
          </div>
        </Card>
      )}

      {!data ? <span className="fm-text-muted">Laster…</span>
        : fane === 'dagbok' ? <Dagbok data={data} />
        : fane === 'hovedbok' ? <Hovedbok data={data} />
        : fane === 'reskontro' ? <Reskontro data={data} />
        : <MvaSpec data={data} />}
    </>
  );
};

const Dagbok = ({ data }) => (
  <Card style={{ padding: 0 }}>
    <table>
      <thead><tr><th>Dato</th><th>Beskrivelse</th><th style={{ textAlign: 'right' }}>Debet</th><th style={{ textAlign: 'right' }}>Kredit</th></tr></thead>
      <tbody>
        {(data.posteringer || []).map((p) => {
          const d = p.linjer.reduce((s, l) => s + l.debet, 0);
          const k = p.linjer.reduce((s, l) => s + l.kredit, 0);
          return (
            <React.Fragment key={p.id}>
              <tr style={{ background: 'var(--theme-bg-secondary)' }}>
                <td>{dato(p.dato)}</td>
                <td><strong>#{p.id}</strong> {p.beskrivelse}</td>
                <td style={{ textAlign: 'right' }}>{kr(d)}</td>
                <td style={{ textAlign: 'right' }}>{kr(k)}</td>
              </tr>
              {p.linjer.map((l) => (
                <tr key={l.id}>
                  <td></td>
                  <td style={{ paddingLeft: 24 }} className="fm-text-muted">
                    {l.konto_nummer} {l.beskrivelse}
                    {l.mva_kode !== '0' && <span className="badge badge-info" style={{ marginLeft: 6 }}>MVA {l.mva_kode}</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>{l.debet ? kr(l.debet) : ''}</td>
                  <td style={{ textAlign: 'right' }}>{l.kredit ? kr(l.kredit) : ''}</td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
        <tr style={{ borderTop: '2px solid var(--theme-border)', fontWeight: 700 }}>
          <td colSpan={2}>Sum</td>
          <td style={{ textAlign: 'right' }}>{kr(data.sum_debet)}</td>
          <td style={{ textAlign: 'right' }}>{kr(data.sum_kredit)}</td>
        </tr>
      </tbody>
    </table>
  </Card>
);

const Hovedbok = ({ data }) => (
  <>
    {(!data.kontoer || data.kontoer.length === 0) && <Card><p className="fm-text-muted">Ingen bevegelser i perioden.</p></Card>}
    {(data.kontoer || []).map((k) => (
      <Card key={k.konto.nummer} style={{ marginBottom: 12, padding: 0 }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--theme-border)',
                      display: 'flex', justifyContent: 'space-between' }}>
          <div><strong>{k.konto.nummer}</strong> – {k.konto.navn}</div>
          <div className="fm-text-muted" style={{ fontSize: 12 }}>
            Debet {kr(k.debet)} | Kredit {kr(k.kredit)} | <strong>Saldo {kr(k.saldo)}</strong>
          </div>
        </div>
        <table>
          <thead><tr><th>Dato</th><th>Beskrivelse</th><th style={{ textAlign: 'right' }}>Debet</th><th style={{ textAlign: 'right' }}>Kredit</th></tr></thead>
          <tbody>
            {k.linjer.map((l, i) => (
              <tr key={i}>
                <td>{dato(l.dato)}</td>
                <td>#{l.postering_id} {l.beskrivelse}</td>
                <td style={{ textAlign: 'right' }}>{l.debet ? kr(l.debet) : ''}</td>
                <td style={{ textAlign: 'right' }}>{l.kredit ? kr(l.kredit) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    ))}
  </>
);

const Reskontro = ({ data }) => (
  <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
    <Card style={{ padding: 0 }}>
      <h3 style={{ margin: 0, padding: '10px 14px', fontSize: 13, borderBottom: '1px solid var(--theme-border)' }}>Kunder</h3>
      <table>
        <thead><tr><th>Navn</th><th>Orgnr</th><th style={{ textAlign: 'right' }}>Utestående</th></tr></thead>
        <tbody>
          {(data.kunder || []).map((k) => (
            <tr key={k.id}>
              <td>{k.navn}</td>
              <td>{k.orgnr}</td>
              <td style={{ textAlign: 'right' }}>{kr(k.utestaende)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
    <Card style={{ padding: 0 }}>
      <h3 style={{ margin: 0, padding: '10px 14px', fontSize: 13, borderBottom: '1px solid var(--theme-border)' }}>Leverandører</h3>
      <table>
        <thead><tr><th>Navn</th><th>Orgnr</th><th style={{ textAlign: 'right' }}>Skyldig</th></tr></thead>
        <tbody>
          {(data.leverandorer || []).map((k) => (
            <tr key={k.id}>
              <td>{k.navn}</td>
              <td>{k.orgnr}</td>
              <td style={{ textAlign: 'right' }}>{kr(k.utestaende)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  </div>
);

const MvaSpec = ({ data }) => (
  <>
    <Card style={{ padding: 0, marginBottom: 12 }}>
      <table>
        <thead><tr><th>Kode</th><th>Beskrivelse</th><th>Sats</th><th>Retning</th>
          <th style={{ textAlign: 'right' }}>Grunnlag</th><th style={{ textAlign: 'right' }}>MVA-beløp</th></tr></thead>
        <tbody>
          {(data.rader || []).map((r) => (
            <tr key={r.kode}>
              <td><strong>{r.kode}</strong></td>
              <td>{r.beskrivelse}</td>
              <td>{r.sats}%</td>
              <td>{r.retning}</td>
              <td style={{ textAlign: 'right' }}>{kr(r.grunnlag)}</td>
              <td style={{ textAlign: 'right' }}>{kr(r.mva)}</td>
            </tr>
          ))}
          {(!data.rader || data.rader.length === 0) && <tr><td colSpan={6} className="fm-text-muted" style={{ textAlign: 'center', padding: 16 }}>Ingen MVA i perioden.</td></tr>}
        </tbody>
      </table>
    </Card>
    <Card>
      <div className="flex justify-between" style={{ fontSize: 14 }}>
        <div>Sum utgående MVA: <strong>{kr(data.sum_utgaende || 0)}</strong></div>
        <div>Sum inngående MVA: <strong>{kr(data.sum_inngaende || 0)}</strong></div>
        <div>MVA å betale: <strong style={{ color: 'var(--theme-accent)' }}>{kr(data.skyldig || 0)}</strong></div>
      </div>
    </Card>
  </>
);

export default Bokforing;
